import type { DiagnosticsService } from "./diagnostics-service";
import {
  bindHoldToOpen,
  TwoContactProbe,
  type DiagnosticsGestureBinding,
  type TimerApi,
} from "./diagnostics-gestures";
import {
  createDiagnosticsReport,
  exportDiagnosticsJson,
  type DiagnosticsRuntimeContext,
} from "./export-report";

export interface DiagnosticsAccessOptions {
  root: HTMLElement;
  trigger: HTMLElement;
  document: Document;
  scope: Window;
  diagnostics: DiagnosticsService;
  getContext: () => DiagnosticsRuntimeContext;
  timers?: TimerApi;
}

function browserTimers(scope: Window): TimerApi {
  return {
    setTimeout: (callback, delayMs) => scope.setTimeout(callback, delayMs),
    clearTimeout: (id) => scope.clearTimeout(id),
  };
}

export function installDiagnosticsAccess(options: DiagnosticsAccessOptions): DiagnosticsGestureBinding {
  const { root, trigger, document, scope, diagnostics, getContext } = options;
  const timers = options.timers ?? browserTimers(scope);
  let panel: HTMLElement | null = null;
  let destroyed = false;
  const panelTimers = new Set<number>();

  const clearPanelTimers = (): void => {
    for (const id of panelTimers) scope.clearTimeout(id);
    panelTimers.clear();
  };

  const closePanel = (): void => {
    clearPanelTimers();
    panel?.remove();
    panel = null;
  };

  const openPanel = (): void => {
    if (destroyed || panel) return;
    const report = createDiagnosticsReport(scope, diagnostics, getContext());
    const json = exportDiagnosticsJson(report);

    const overlay = document.createElement("section");
    overlay.className = "diagnostics-panel";
    overlay.dataset.diagnostics = "true";

    const header = document.createElement("header");
    header.className = "diagnostics-header";
    const title = document.createElement("h2");
    title.textContent = "Tesla Browser Diagnostics";
    const close = document.createElement("button");
    close.type = "button";
    close.className = "diagnostics-control";
    close.textContent = "Close";
    close.addEventListener("click", closePanel);
    header.append(title, close);

    const touchTest = document.createElement("div");
    touchTest.className = "diagnostics-touch-test game-surface";
    touchTest.dataset.twoContactTest = "true";
    touchTest.dataset.twoContactState = "waiting";
    touchTest.textContent = "Hold two fingers here together";
    const probe = new TwoContactProbe(750);

    const updateProbe = (): void => {
      if (!panel || touchTest.dataset.twoContactState === "pass") return;
      if (probe.update(scope.performance.now())) {
        touchTest.dataset.twoContactState = "pass";
        touchTest.textContent = "Two-contact input passed";
      }
    };
    const onPointerDown = (event: PointerEvent): void => {
      event.preventDefault();
      probe.press(event.pointerId, scope.performance.now());
      try { touchTest.setPointerCapture(event.pointerId); } catch { /* capture is optional */ }
      const id = scope.setTimeout(() => {
        panelTimers.delete(id);
        updateProbe();
      }, 750);
      panelTimers.add(id);
    };
    const onPointerRelease = (event: PointerEvent): void => {
      event.preventDefault();
      probe.release(event.pointerId);
      updateProbe();
    };
    touchTest.addEventListener("pointerdown", onPointerDown, { passive: false });
    touchTest.addEventListener("pointerup", onPointerRelease, { passive: false });
    touchTest.addEventListener("pointercancel", onPointerRelease, { passive: false });
    touchTest.addEventListener("lostpointercapture", onPointerRelease);

    const actions = document.createElement("div");
    actions.className = "diagnostics-actions";
    const copy = document.createElement("button");
    copy.type = "button";
    copy.className = "diagnostics-control";
    copy.dataset.copyDiagnostics = "true";
    copy.textContent = "Copy JSON";
    copy.addEventListener("click", () => {
      const writeText = scope.navigator.clipboard?.writeText.bind(scope.navigator.clipboard);
      if (!writeText) return;
      void writeText(json).then(
        () => { copy.textContent = "Copied"; },
        () => { copy.textContent = "Copy failed"; },
      );
    });
    actions.append(copy);

    const reportView = document.createElement("pre");
    reportView.className = "diagnostics-report";
    reportView.dataset.diagnosticsReport = "true";
    reportView.textContent = json;

    overlay.append(header, touchTest, actions, reportView);
    root.append(overlay);
    panel = overlay;
  };

  const holdBinding = bindHoldToOpen(trigger, openPanel, timers, 2000);

  return {
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      holdBinding.destroy();
      closePanel();
    },
  };
}
